/**
 * The filter bar. Everything here writes straight to the URL — see useUrlFilters for why.
 *
 * The fifteen type chips live behind a popover rather than sitting inline. Inline they took most of
 * the bar's width, wrapped onto a second row on a laptop, and pushed the search box — the control
 * people actually reach for first — into a corner.
 *
 * The search box also accepts a log type, using the plugin's own alias table (`money`, `duels`,
 * `ban`, …). Somebody who already knows `/debug <player> money` should get the same result from
 * typing "money" here rather than learning that the website calls it PAYMENT and hides it in a menu.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { searchPlayers } from "../api";
import { useDebounced } from "../hooks/useUrlFilters";
import type { Filters } from "../hooks/useUrlFilters";
import type { LogMeta, LogType } from "../types";
import { typeColor, typeLabel } from "../format";
import { IconChevron } from "./Icons";

interface Props {
  filters: Filters;
  setFilters: (next: Partial<Filters>) => void;
  reset: () => void;
  meta: LogMeta | undefined;
}

export function FilterBar({ filters, setFilters, reset, meta }: Props): JSX.Element {
  const [searchText, setSearchText] = useState(filters.q);
  const [playerText, setPlayerText] = useState(filters.player);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [typesOpen, setTypesOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounced(searchText, 250);
  const debouncedPlayer = useDebounced(playerText, 250);

  // Alias -> canonical type, built from what the server reports so the two lists cannot drift.
  const aliasMap = useMemo(() => {
    const map = new Map<string, LogType>();
    for (const entry of meta?.types ?? []) {
      map.set(entry.type.toLowerCase().replaceAll("_", ""), entry.type);
      for (const alias of entry.aliases) map.set(alias, entry.type);
    }
    return map;
  }, [meta]);

  useEffect(() => {
    if (debouncedSearch !== filters.q) setFilters({ q: debouncedSearch });
  }, [debouncedSearch, filters.q, setFilters]);

  useEffect(() => {
    if (debouncedPlayer !== filters.player) setFilters({ player: debouncedPlayer });
  }, [debouncedPlayer, filters.player, setFilters]);

  // Autocomplete. Aborted when the term changes, so a slow response for "Kaz" cannot land after
  // "Kazoo" and overwrite the newer suggestions.
  useEffect(() => {
    if (debouncedPlayer.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const controller = new AbortController();
    searchPlayers(debouncedPlayer.trim(), controller.signal)
      .then((result) => setSuggestions(result.players))
      .catch(() => undefined);
    return () => controller.abort();
  }, [debouncedPlayer]);

  // `/` focuses search, the way it does in every other tool staff use all day.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.key === "/") {
        event.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close the type popover on an outside click or Escape.
  useEffect(() => {
    if (!typesOpen) return;
    const onDown = (event: MouseEvent): void => {
      if (!popRef.current?.contains(event.target as Node)) setTypesOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setTypesOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [typesOpen]);

  const toggleType = (type: LogType): void => {
    setFilters({
      types: filters.types.includes(type)
        ? filters.types.filter((t) => t !== type)
        : [...filters.types, type],
    });
  };

  /** If what was typed is a type name or alias, offer it as a filter rather than searching message
   *  bodies for the word "money". */
  const typeSuggestion = aliasMap.get(searchText.trim().toLowerCase().replaceAll(/[-_ ]/g, ""));

  const active =
    filters.player || filters.types.length > 0 || filters.server || filters.from || filters.to || filters.q;

  return (
    <div className="filterbar">
      <input
        ref={searchRef}
        className="input search"
        placeholder="Search messages…    /"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        aria-label="Search log messages"
      />

      {typeSuggestion && !filters.types.includes(typeSuggestion) && (
        <button
          type="button"
          className="btn"
          data-variant="primary"
          onClick={() => {
            toggleType(typeSuggestion);
            setSearchText("");
          }}
          title="Filter by this log type instead of searching for the word"
        >
          Filter: {typeLabel(typeSuggestion)}
        </button>
      )}

      <input
        className="input"
        list="player-suggestions"
        placeholder="Player"
        value={playerText}
        onChange={(event) => setPlayerText(event.target.value)}
        aria-label="Filter by player"
        style={{ width: "15ch" }}
      />
      <datalist id="player-suggestions">
        {suggestions.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>

      <div className="popwrap" ref={popRef}>
        <button
          type="button"
          className="btn"
          data-active={filters.types.length > 0}
          aria-expanded={typesOpen}
          onClick={() => setTypesOpen((open) => !open)}
        >
          {filters.types.length === 0 ? "All types" : `${filters.types.length} types`}
          <IconChevron />
        </button>

        {typesOpen && (
          <div className="pop">
            <div className="pop-head">
              <b>Log types</b>
              <span className="grow" />
              <button type="button" className="btn" onClick={() => setFilters({ types: [] })}>
                Clear
              </button>
            </div>
            <div className="chips">
              {(meta?.types ?? []).map(({ type, aliases }) => (
                <button
                  key={type}
                  type="button"
                  className="chip"
                  aria-pressed={filters.types.includes(type)}
                  onClick={() => toggleType(type)}
                  style={{ ["--chip" as string]: typeColor(type) }}
                  title={aliases.length > 0 ? `also: ${aliases.join(", ")}` : type}
                >
                  {typeLabel(type)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <select
        className="input"
        value={filters.server}
        onChange={(event) => setFilters({ server: event.target.value })}
        aria-label="Filter by server"
      >
        <option value="">All servers</option>
        {(meta?.servers ?? []).map((server) => (
          <option key={server} value={server}>
            {server}
          </option>
        ))}
      </select>

      <input
        className="input"
        type="date"
        value={filters.from}
        onChange={(event) => setFilters({ from: event.target.value })}
        aria-label="From date"
        title="From date"
      />
      <input
        className="input"
        type="date"
        value={filters.to}
        onChange={(event) => setFilters({ to: event.target.value })}
        aria-label="To date"
        title="To date"
      />

      {active && (
        <button type="button" className="btn" onClick={reset}>
          Clear all
        </button>
      )}
    </div>
  );
}
